/**
 * SurveyManagement - CRUD for surveys and feedback
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ClipboardList, BarChart3, Loader2, GripVertical } from 'lucide-react';
import { surveyAPI } from '@/lib/api';

const QUESTION_TYPES = ['rating', 'text', 'multiple_choice', 'checkbox', 'nps'];

// Form component extracted outside
const SurveyForm = ({ survey, onSubmit, onCancel }) => {
  const [form, setForm] = useState(survey || { title: '', description: '', questions: [], is_active: true, target_type: 'all' });
  const addQuestion = () => setForm({...form, questions: [...form.questions, { id: Date.now().toString(), question: '', question_type: 'rating', options: [], required: false, order: form.questions.length }]});
  const updateQuestion = (idx, updates) => setForm({...form, questions: form.questions.map((q, i) => i === idx ? {...q, ...updates} : q)});
  const removeQuestion = (idx) => setForm({...form, questions: form.questions.filter((_, i) => i !== idx)});
  
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Post-Event Survey" /></div>
      <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={2} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Target</Label>
          <Select value={form.target_type} onValueChange={v => setForm({...form, target_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Attendees</SelectItem>
              <SelectItem value="session">Specific Session</SelectItem>
              <SelectItem value="speaker">Specific Speaker</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-6"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label>Active</Label></div>
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-base font-semibold">Questions</Label>
          <Button type="button" variant="outline" size="sm" onClick={addQuestion}><Plus className="h-4 w-4 mr-1" />Add Question</Button>
        </div>
        <div className="space-y-4">
          {form.questions.map((q, idx) => (
            <Card key={q.id} className="p-3">
              <div className="flex items-start gap-2">
                <GripVertical className="h-5 w-5 text-slate-400 mt-2 cursor-move" />
                <div className="flex-1 space-y-2">
                  <Input value={q.question} onChange={e => updateQuestion(idx, { question: e.target.value })} placeholder="Question text..." />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <Select value={q.question_type} onValueChange={v => updateQuestion(idx, { question_type: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{QUESTION_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-1"><Switch checked={q.required} onCheckedChange={v => updateQuestion(idx, { required: v })} /><span className="text-xs">Required</span></div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeQuestion(idx)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  {(q.question_type === 'multiple_choice' || q.question_type === 'checkbox') && (
                    <div>
                      <Input placeholder="Options (comma separated)" value={q.options?.join(', ') || ''} onChange={e => updateQuestion(idx, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} className="h-8 text-sm" />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.title || form.questions.length === 0}>{survey ? 'Update' : 'Create'}</Button>
      </div>
    </div>
  );
};

const SurveyManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [viewingResponses, setViewingResponses] = useState(null);

  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ['surveys', eventId],
    queryFn: () => surveyAPI.list(eventId).then(r => r.data),
  });

  // Transform frontend format to backend format
  const transformSurveyForBackend = (data) => ({
    ...data,
    questions: data.questions.map(q => ({
      text: q.question,
      type: q.question_type,
      options: q.options || [],
      required: q.required || false,
    }))
  });

  const createMutation = useMutation({
    mutationFn: (data) => surveyAPI.create(eventId, transformSurveyForBackend(data)),
    onSuccess: () => { toast.success('Survey created'); queryClient.invalidateQueries(['surveys', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to create survey')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => surveyAPI.update(eventId, id, transformSurveyForBackend(data)),
    onSuccess: () => { toast.success('Survey updated'); queryClient.invalidateQueries(['surveys', eventId]); setEditingSurvey(null); },
    onError: (err) => toast.error(err.response?.data?.message || err.response?.data?.detail || 'Failed to update survey')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => surveyAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Survey deleted'); queryClient.invalidateQueries(['surveys', eventId]); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const { data: responses = [] } = useQuery({
    queryKey: ['survey-responses', eventId, viewingResponses?.id],
    queryFn: () => surveyAPI.getResponses(eventId, viewingResponses.id).then(r => r.data),
    enabled: !!viewingResponses,
  });

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="survey-management">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="h-6 w-6" />Surveys & Feedback</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Create Survey</Button></DialogTrigger>
          <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Create Survey</DialogTitle></DialogHeader>
            <SurveyForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {surveys.map(survey => (
          <Card key={survey.id} data-testid={`survey-${survey.id}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{survey.title}</CardTitle>
                  <p className="text-sm text-slate-500">{survey.questions?.length || 0} questions</p>
                </div>
                <Badge variant={survey.is_active ? 'default' : 'secondary'}>{survey.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {survey.description && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{survey.description}</p>}
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="flex items-center gap-1"><BarChart3 className="h-3 w-3" />{survey.responses_count || 0} responses</Badge>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setViewingResponses(survey)}><BarChart3 className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditingSurvey(survey)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(survey.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {surveys.length === 0 && <p className="col-span-3 text-center text-slate-500 py-8">No surveys created yet.</p>}
      </div>

      <Dialog open={!!editingSurvey} onOpenChange={() => setEditingSurvey(null)}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Edit Survey</DialogTitle></DialogHeader>
          {editingSurvey && <SurveyForm survey={editingSurvey} onSubmit={data => updateMutation.mutate({ id: editingSurvey.id, data })} onCancel={() => setEditingSurvey(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingResponses} onOpenChange={() => setViewingResponses(null)}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>Survey Responses: {viewingResponses?.title}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {responses.length === 0 ? <p className="text-center text-slate-500 py-8">No responses yet.</p> : (
              <div className="space-y-4">{responses.map((r, idx) => (
                <Card key={r.id} className="p-3">
                  <div className="text-xs text-slate-500 mb-2">Response #{idx + 1} • {new Date(r.submitted_at).toLocaleString()}</div>
                  {Object.entries(r.answers || {}).map(([qId, ans]) => {
                    const q = viewingResponses.questions?.find(x => x.id === qId);
                    return <div key={qId} className="mb-2"><span className="font-medium text-sm">{q?.question || qId}:</span> <span className="text-sm">{Array.isArray(ans) ? ans.join(', ') : ans}</span></div>;
                  })}
                </Card>
              ))}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurveyManagement;
